import React, { useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { ArrowBack, CheckCircle } from '@material-ui/icons';
import { toast } from 'react-toastify';

import { parseStudents, saveStudents } from '../../../../../../services/certificateBatches';
import {
  ActionsRow,
  PasteRow,
  ParseRow,
  UploadLabel,
  WarningText,
  ErrorText,
  StaleNotice,
  EmptyState,
  StyledTableContainer,
} from './styles';

// batchId identifica a turma já criada (Empresa/Certificado/Ementa salvos
// nos passos anteriores). onDone finaliza o wizard e navega pro detalhe da
// turma. `students` do preview já vem deduplicado pelo backend, com
// id_pessoa preenchido quando o e-mail bate com um cadastro existente.
export default function StudentsStep({ batchId, onBack, onDone }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null); // {students, warnings, errors}
  const [previewedText, setPreviewedText] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Se o usuário mexeu no texto depois de gerar o preview, a lista mostrada
  // (e a que seria salva) não corresponde mais ao que está na tela.
  const isStale = preview !== null && text !== previewedText;

  const handleParse = async (file = null) => {
    setIsParsing(true);
    try {
      const { data } = await parseStudents(batchId, { text, file });
      setPreview(data);
      setPreviewedText(text);
    } catch (error) {
      toast.error('Não foi possível processar a lista. Confira o formato e tente novamente.');
    } finally {
      setIsParsing(false);
      // Limpa o input pra permitir re-selecionar o mesmo arquivo depois de
      // corrigi-lo.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) handleParse(file);
  };

  const handleConfirm = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await saveStudents(batchId, preview.students);
      onDone();
    } catch (error) {
      toast.error('Não foi possível salvar os alunos. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const canConfirm =
    !!preview &&
    !isStale &&
    !isSaving &&
    preview.errors.length === 0 &&
    preview.students.length > 0;

  return (
    <div>
      <PasteRow>
        <TextField
          multiline
          rows={8}
          label="Colar lista de alunos"
          placeholder={'Cole a lista — um aluno por linha:\nNome; email@empresa.com'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
        />
        <ParseRow>
          <Button
            type="button"
            variant="outlined"
            disabled={!text.trim() || isParsing}
            onClick={() => handleParse()}
          >
            {isParsing ? <CircularProgress size={20} /> : 'Pré-visualizar lista colada'}
          </Button>
          <UploadLabel>
            ou planilha:
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              disabled={isParsing}
              onChange={handleFileChange}
            />
          </UploadLabel>
        </ParseRow>
      </PasteRow>

      {isStale && (
        <StaleNotice>
          A lista foi alterada depois da última pré-visualização — pré-visualize novamente antes
          de confirmar.
        </StaleNotice>
      )}

      {preview && !isStale && (
        <div>
          {preview.errors.map((err, i) => (
            <ErrorText key={`err-${i}`}>✖ {err}</ErrorText>
          ))}
          {preview.warnings.map((w, i) => (
            <WarningText key={`warn-${i}`}>⚠ {w}</WarningText>
          ))}

          {preview.students.length === 0 ? (
            <EmptyState>Nenhum aluno reconhecido na lista.</EmptyState>
          ) : (
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Cadastro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.students.map((s, i) => (
                    <TableRow key={s.email}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{s.nome}</TableCell>
                      <TableCell>{s.email}</TableCell>
                      <TableCell>
                        {s.id_pessoa ? `já existe (#${s.id_pessoa})` : 'será criado'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </div>
      )}

      <ActionsRow>
        <Button type="button" startIcon={<ArrowBack />} onClick={onBack} disabled={isSaving}>
          Voltar
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {isSaving ? 'Salvando…' : `Confirmar${preview && preview.students.length ? ` ${preview.students.length} alunos` : ''}`}
        </Button>
      </ActionsRow>
    </div>
  );
}
